<script setup>
import { computed } from 'vue';
import { provideMessageContext } from 'dashboard/components-next/message/provider.js';
import {
  ATTACHMENT_TYPES,
  MESSAGE_TYPES,
  MESSAGE_VARIANTS,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from 'dashboard/components-next/message/constants';
import TextBubble from 'dashboard/components-next/message/bubbles/Text/Index.vue';
import ImageBubble from 'dashboard/components-next/message/bubbles/Image.vue';
import FileBubble from 'dashboard/components-next/message/bubbles/File.vue';
import AudioBubble from 'dashboard/components-next/message/bubbles/Audio.vue';
import VideoBubble from 'dashboard/components-next/message/bubbles/Video.vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  currentUserId: {
    type: Number,
    required: true,
  },
  galleryAttachments: {
    type: Array,
    default: () => [],
  },
  isGroupChat: {
    type: Boolean,
    default: false,
  },
});

const isOutgoing = computed(
  () => props.message.sender?.id === props.currentUserId
);

const variant = computed(() =>
  isOutgoing.value ? MESSAGE_VARIANTS.AGENT : MESSAGE_VARIANTS.USER
);

const orientation = computed(() =>
  isOutgoing.value ? ORIENTATION.RIGHT : ORIENTATION.LEFT
);

const messageType = computed(() =>
  isOutgoing.value ? MESSAGE_TYPES.OUTGOING : MESSAGE_TYPES.INCOMING
);

const content = computed(() => props.message.content || '');
const attachments = computed(() => props.message.attachments || []);
const createdAt = computed(() => props.message.createdAt);
const id = computed(() => props.message.id);
const hasContent = computed(() => !!content.value.trim());

const galleryAttachments = computed(() =>
  useSnakeCase(props.galleryAttachments)
);

const componentToRender = computed(() => {
  if (attachments.value.length === 1 && !hasContent.value) {
    const fileType = attachments.value[0].fileType;

    if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
    if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
    if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
    if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
  }

  return TextBubble;
});

const flexOrientationClass = computed(() =>
  isOutgoing.value ? 'justify-end' : 'justify-start'
);

const showIncomingAvatar = computed(
  () => !isOutgoing.value && props.isGroupChat
);

const gridClass = computed(() => {
  if (isOutgoing.value) return 'grid grid-cols-[1fr_24px]';
  if (showIncomingAvatar.value) return 'grid grid-cols-[24px_1fr]';
  return 'grid grid-cols-1fr';
});

const gridTemplate = computed(() => {
  if (isOutgoing.value) {
    return `
      "bubble avatar"
      "meta spacer"
    `;
  }
  if (showIncomingAvatar.value) {
    return `
      "avatar bubble"
      "spacer meta"
    `;
  }
  return `
    "bubble"
    "meta"
  `;
});

const avatarInfo = computed(() => ({
  name: props.message.sender?.name ?? '',
  src: props.message.sender?.thumbnail ?? '',
}));

provideMessageContext({
  id,
  content,
  attachments,
  createdAt,
  messageType,
  isStaffChat: computed(() => true),
  status: computed(() => MESSAGE_STATUS.SENT),
  private: computed(() => false),
  isPrivate: computed(() => false),
  contentAttributes: computed(() => ({})),
  contentType: computed(() => CONTENT_TYPES.TEXT),
  variant,
  orientation,
  isBotOrAgentMessage: isOutgoing,
  shouldGroupWithNext: computed(() => false),
  inReplyTo: computed(() => null),
  sourceId: computed(() => ''),
  sender: computed(() => props.message.sender),
  senderId: computed(() => props.message.sender?.id ?? null),
  senderType: computed(() => 'User'),
  conversationId: computed(() => props.message.staffChatId),
  currentUserId: computed(() => props.currentUserId),
  inboxId: computed(() => null),
  galleryAttachments,
});
</script>

<template>
  <div class="flex w-full mb-2" :class="flexOrientationClass">
    <div
      class="gap-x-2 max-w-lg"
      :class="gridClass"
      :style="{ gridTemplateAreas: gridTemplate }"
    >
      <div
        v-if="showIncomingAvatar"
        v-tooltip.right-end="avatarInfo.name"
        class="[grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" rounded-full />
      </div>
      <div
        class="[grid-area:bubble] flex"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
        }"
      >
        <component :is="componentToRender" />
      </div>
      <div v-if="isOutgoing" class="[grid-area:avatar] flex items-end">
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
    </div>
  </div>
</template>
